import { StaticImageData } from 'next/image';
import React from 'react';
import { VideoData } from '@/types';
import styles from './VideoPreview.module.scss';

export const VideoPreview: React.FC<VideoData> = React.memo(
	({ videoSrc, animatedWebPSrc, thumbnail }) => {
		const [isMediaLoaded, setIsMediaLoaded] = React.useState(false);
		const videoRef = React.useRef<HTMLVideoElement>(null);
		const imgRef = React.useRef<HTMLImageElement>(null);

		const handleMediaLoad = () => {
			setIsMediaLoaded(true);
		};

		const handleVideoError = () => {
			console.error('Video failed to load');
		};

		React.useEffect(() => {
			if (animatedWebPSrc) {
				if (imgRef.current?.complete) {
					setIsMediaLoaded(true);
				}
				return;
			}

			const videoElement = videoRef.current;
			if (!videoElement) return;

			// Safari specific inline playback
			videoElement.setAttribute('webkit-playsinline', 'true');
			videoElement.setAttribute('playsinline', 'true');
			videoElement.setAttribute('muted', 'true');
			videoElement.setAttribute('disableRemotePlayback', '');

			if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
				setIsMediaLoaded(true);
			}

			void videoElement.play().catch(() => {
				console.error('Autoplay failed');
			});
		}, [animatedWebPSrc]);

		const mediaClassName = `${styles['video-preview__video']} ${
			isMediaLoaded ? styles['video-preview__video--loaded'] : ''
		}`;

		return (
			<div className={styles['video-preview__container']}>
				{thumbnail && (
					<img
						src={thumbnail.src}
						alt='Loading'
						className={styles['video-preview__placeholder']}
					/>
				)}

				{animatedWebPSrc ? (
					<img
						ref={imgRef}
						src={animatedWebPSrc.src}
						alt=''
						className={mediaClassName}
						onLoad={handleMediaLoad}
					/>
				) : (
					<video
						ref={videoRef}
						className={mediaClassName}
						onLoadedMetadata={handleMediaLoad}
						onError={handleVideoError}
						autoPlay
						loop
						muted
						playsInline
						preload='auto'
						webkit-playsinline='true'
					>
						{videoSrc && <source src={videoSrc} type='video/mp4' />}
					</video>
				)}
			</div>
		);
	},
);
